import React, { useContext, useState } from 'react';
import { PreferenceInput } from 'sr-types/lib/account/v1/graphql';
import { Preference } from 'sr-types/lib/search/v1/graphql';
import { useSavePreferences } from '../../../common/auth/hooks/savePreferences';
import { UserContext } from '../../../common/auth/UserContext';
import { ENTITIES, getCustomSortRankOrderFieldNames } from '../../../common/constants/customSortFieldConfigs';
import { FormProvider } from '../../../common/form/FormContext';
import FormModal from '../../../common/form/FormModal';
import type { ReportConfigurations } from '../../../common/generateReport/reportTypes';
import { buildSortTokens, getOptionId } from '../../../common/generateReport/reportUtils';
import I18n from '../../../common/i18n/I18n';
import { PROD_DATA_LIST_KEY, PROD_DATA_LIST_NAME } from './productionDataListConfigConstants';
import { ProductionDataListConfigFormPanel } from './ProductionDataListConfigFormPanel';
import { getWorkspaceDataCardPreference } from './productionDataListConfigurationUtils';
import { ProductionDataListConfigurationState } from './types';

export type ProductionDataListConfigurationProps = {
    entity: string;
    handleConfigurationModalState: (isOpen: boolean) => void;
    setCustomVariables?: any;
};

const ProductionDataListConfiguration = ({
    entity,
    handleConfigurationModalState,
    setCustomVariables
}: ProductionDataListConfigurationProps) => {
    const { userProfile, preferences, refreshPreferences } = useContext(UserContext);
    const { savePreferences } = useSavePreferences();

    const { workspacePreferenceValue, workspacePreferences } = getWorkspaceDataCardPreference(preferences || []);
    const rankFieldNames = getCustomSortRankOrderFieldNames(ENTITIES.workspace);
    const rankOrderValues = Object.fromEntries(
        rankFieldNames.map((name) => [name, workspacePreferenceValue[name] || []])
    );

    const [values, setValues] = useState<ProductionDataListConfigurationState>({
        sortWorkspacesBy: workspacePreferenceValue.sortWorkspacesBy || '',
        keyPeopleGroupBy: workspacePreferenceValue.keyPeopleGroupBy || '',
        keyOrganizationsGroupBy: workspacePreferenceValue.keyOrganizationsGroupBy || '',
        projectOwnersGroupBy: workspacePreferenceValue.projectOwnersGroupBy || '',
        workspaceGroupBy: workspacePreferenceValue.workspaceGroupBy || [],
        ...rankOrderValues,
        sortCohortsBy: workspacePreferenceValue.sortCohortsBy || 'publictitle_ASC'
    });
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const onSavePreferences = async () => {
        setIsSaving(true);

        const preferenceInput: PreferenceInput = {
            ...(workspacePreferences?.id ? { identity: { id: workspacePreferences.id } } : {}),
            accountId: userProfile?.accountId || '',
            key: PROD_DATA_LIST_KEY,
            name: PROD_DATA_LIST_NAME,
            value: JSON.stringify(values)
        };

        try {
            const preference = await savePreferences({ preferenceInput, shareInput: null });

            if (preference.id) {
                handleConfigurationModalState(false);

                if (values.sortWorkspacesBy !== '') {
                    let sortByValue = values.sortWorkspacesBy;

                    // If custom sort is selected, build the sort tokens from workspace group selections and their rank orders instead of using the 'custom' literal value.
                    if (getOptionId(sortByValue) === 'custom' && values.workspaceGroupBy) {
                        const rankOrderReportConfig = Object.fromEntries(
                            rankFieldNames.map((name) => [name, values[name] || []])
                        );

                        const reportConfig: ReportConfigurations = {
                            workspaceGroupBy: values.workspaceGroupBy || [],
                            ...rankOrderReportConfig,
                            sortWorkspacesBy: values.sortWorkspacesBy || '',
                            sortCohortsBy: values.sortCohortsBy || ''
                        };
                        const sortTokens = buildSortTokens(values.workspaceGroupBy, reportConfig);
                        sortByValue = sortTokens.join(',');
                    }

                    setCustomVariables?.({ sortBy: sortByValue });
                }

                const updatedPreferences = workspacePreferences
                    ? preferences.map((pref) =>
                          pref.id === workspacePreferences.id
                              ? { ...workspacePreferences, value: JSON.stringify(values) }
                              : pref
                      )
                    : [
                          ...preferences,
                          {
                              id: preference.id,
                              accountId: userProfile?.accountId || '',
                              key: PROD_DATA_LIST_KEY,
                              value: JSON.stringify(values),
                              name: PROD_DATA_LIST_NAME
                          } as Preference
                      ];

                refreshPreferences(undefined, updatedPreferences, false);
            }
        } catch (error) {
            console.warn('Error saving preferences:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <FormModal
            id={`${entity}_settings_modal`}
            title={<I18n token="entity.settings.title" values={{ entity }} />}
            isOpen
            width={'60%'}
            onClose={() => handleConfigurationModalState(false)}
            onSave={onSavePreferences}
            isSaving={isSaving}
        >
            <FormProvider state={values} setState={setValues}>
                <ProductionDataListConfigFormPanel />
            </FormProvider>
        </FormModal>
    );
};

export default ProductionDataListConfiguration;
